import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";

export default function MessagesNavBell() {
  const { token } = useAuth();
  const [count, setCount] = useState(0);

  // stable token
  const kc_token = useMemo(
    () => token || localStorage.getItem("kc_token") || "",
    [token]
  );

  // helper to safely set count (always apply zero)
  const setSafe = (v) => setCount(Number.isFinite(Number(v)) ? Number(v) : 0);

  // single fetch function (reused by effects & events)
  const abortRef = useRef();
  const pull = async () => {
    if (!kc_token) return setSafe(0);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const r = await fetch("/messaging/unread-count", {
        headers: { Authorization: `Bearer ${kc_token}` },
        signal: ac.signal,
      });
      const d = r.ok ? await r.json() : { count: 0 };
      setSafe(d.count);
    } catch {
      // on error still force a number (keeps UI consistent)
      setSafe(count);
    }
  };

  // initial + token change
  useEffect(() => { pull(); /* eslint-disable-next-line */ }, [kc_token]);

  // listen app events from MessagesPage (including count=0)
  useEffect(() => {
    const handler = (e) => setSafe(e?.detail?.count);
    window.addEventListener("messaging:unread", handler);
    return () => window.removeEventListener("messaging:unread", handler);
  }, []);

  // keep in sync on tab focus / visibility change
  useEffect(() => {
    const onFocus = () => pull();
    const onVis   = () => document.visibilityState === "visible" && pull();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      abortRef.current?.abort?.();
    };
  }, []);

  return (
    <a
      href="/messages"
      className="nav-link text-dark d-flex justify-content-center icon-btn position-relative"
      title="Messages"
      aria-label="Messages"
    >
      <i className="bi bi-chat" style={{ fontSize: "1.5rem" }} />
      {count > 0 && (
        <span
          className="position-absolute rounded-circle bg-danger text-white fw-bold"
          style={{
            top: "4px", right: "6px", fontSize: "0.7rem",
            minWidth: "18px", height: "18px", lineHeight: "18px",
            textAlign: "center", boxShadow: "0 0 4px rgba(0,0,0,0.2)",
          }}
        >
          {count > 9 ? "9+" : count}
        </span>
      )}
    </a>
  );
}
